// src/features/executive/insight/widgets/GenderDonut.tsx
import { Chip, Typography } from '@mui/material'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import { useTranslation } from 'react-i18next'
import CategoricalDonut from './CategoricalDonut'
import { useGender } from '../hooks/use-gender'
import type { GenderKey } from 'api/executive/executive.types'

const COLOR: Record<GenderKey, string> = {
  M: '#2a8d9c',
  F: '#e67e22',
  U: '#95a5a6',
}

// Surface a data-quality nudge when too many patient records lack a gender —
// usually a sign the register form is letting clinic staff skip the field.
const UNSPECIFIED_WARNING_PCT = 15

export default function GenderDonut() {
  const { t } = useTranslation()
  const { data, loading, error, refetch } = useGender()

  const segments =
    data?.segments.map((s) => ({
      ...s,
      label: t(`EXECUTIVE.INSIGHT.GENDER.${s.key}`),
    })) ?? []

  const unspecified = segments.find((s) => s.key === 'U')
  const showUnspecifiedWarning =
    (unspecified?.percent ?? 0) > UNSPECIFIED_WARNING_PCT

  const notice = showUnspecifiedWarning ? (
    <Chip
      icon={<WarningAmberOutlinedIcon />}
      label={
        <Typography variant="caption" sx={{ whiteSpace: 'normal' }}>
          {t(
            'EXECUTIVE.INSIGHT.GENDER.UNSPECIFIED_WARNING',
            'ไม่ระบุเพศ {{pct}}% — โปรดตรวจสอบการบันทึกข้อมูลในแบบฟอร์มลงทะเบียน',
            { pct: (unspecified?.percent ?? 0).toFixed(0) }
          )}
        </Typography>
      }
      color="warning"
      variant="outlined"
      size="small"
      sx={{ height: 'auto', '& .MuiChip-label': { px: 1, py: 0.25 } }}
    />
  ) : null

  return (
    <CategoricalDonut
      title={t('EXECUTIVE.INSIGHT.GENDER.TITLE')}
      titleTooltip={t(
        'EXECUTIVE.INSIGHT.GENDER.TOOLTIP',
        'นับจำนวนคนไข้ (unique) ที่มีรายการในช่วงวันที่เลือก แยกตามเพศ — 1 คนไข้ที่มาหลายครั้งนับเป็น 1 ราย\n"ไม่ระบุ" คือคนไข้ที่ระบบไม่มีข้อมูลเพศใน profile'
      )}
      emptyMessage={t(
        'EXECUTIVE.INSIGHT.GENDER.EMPTY',
        'ไม่พบคนไข้ในช่วงเวลาและเงื่อนไขที่เลือก'
      )}
      notice={notice}
      segments={segments}
      total={data?.total ?? 0}
      previousTotal={data?.previousTotal ?? null}
      colorMap={COLOR}
      centerLabel={t('EXECUTIVE.INSIGHT.TOTAL_PATIENTS')}
      unit={t('EXECUTIVE.INSIGHT.UNIT.PATIENT')}
      loading={loading}
      error={error}
      onRetry={refetch}
    />
  )
}
